import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Typography,
  Statistic,
  Tag,
  Space,
  Alert,
  Divider,
  message,
  Avatar,
  Modal,
  Checkbox,
  Input,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  EyeOutlined,
  WarningOutlined,
  CopyOutlined,
  StopOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axiosInstance from '../../../../config/axiosConfig';
import { useWebSocketClientSafe } from '../../../../context/websocketContext';
import { AudienceBadge, RedactedRecipientsTable } from '../../../../components/audience';
import './TemplateBuilder.css';
import './AdvancedTemplateBuilder.css';
import './CampaignDetailsPage.css';

const { Title, Text } = Typography;

const normalizeScheduleStatus = (value) => String(value || '').toLowerCase();

const getScheduleStatusLabel = (status) => ({
  scheduled: 'Scheduled',
  queued: 'Queued',
  processing: 'Processing',
  audience_fetching: 'Audience Fetching',
  completed: 'Sent',
}[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));

const getCampaignDisplayStatus = (campaign = {}) => {
  const scheduleStatus = normalizeScheduleStatus(campaign.schedule_status || campaign.scheduleStatus);
  if (campaign.issent === 1 || scheduleStatus === 'completed') {
    return { label: 'Sent', className: 'cdp__badge--sent', scheduleStatus };
  }
  if (['scheduled', 'queued', 'processing', 'audience_fetching'].includes(scheduleStatus)) {
    return { label: getScheduleStatusLabel(scheduleStatus), className: 'cdp__badge--scheduled', scheduleStatus };
  }
  return { label: 'Draft', className: 'cdp__badge--draft', scheduleStatus };
};

const CampaignDetailsPage = ({ campaignId, onBack, campaignData, onCampaignUpdated }) => {
  // Skeletal Loading Components with prefers-reduced-motion support
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const skeletonAnimation = prefersReducedMotion ? 'none' : 'broadcast-skeleton-loading 1s infinite ease-in-out';

  const CampaignDetailsSkeleton = () => (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Compact Header Skeleton */}
      <Card style={{ marginBottom: 16, padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            animation: skeletonAnimation
          }} />
          <div style={{ flex: 1 }}>
            <div style={{
              height: 20,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              borderRadius: 4,
              marginBottom: 6,
              width: '200px',
              animation: skeletonAnimation
            }} />
            <div style={{
              height: 14,
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              borderRadius: 4,
              width: '120px',
              animation: skeletonAnimation
            }} />
          </div>
        </div>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Left Column - Stats and Recipients Skeleton */}
        <Col xs={24} lg={14}>
          {/* Campaign Statistics Skeleton */}
          <Card title="Campaign Statistics" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Col xs={12} sm={6} key={index}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      height: 32,
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                      borderRadius: 6,
                      marginBottom: 8,
                      animation: skeletonAnimation
                    }} />
                    <div style={{
                      height: 16,
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                      borderRadius: 4,
                      width: '70%',
                      margin: '0 auto',
                      animation: skeletonAnimation
                    }} />
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Campaign Information Skeleton */}
          <Card title="Campaign Information" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index}>
                      <div style={{
                        height: 14,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        borderRadius: 4,
                        width: '80px',
                        marginBottom: 6,
                        animation: skeletonAnimation
                      }} />
                      <div style={{
                        height: 18,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        borderRadius: 4,
                        width: `${120 + Math.random() * 60}px`,
                        animation: skeletonAnimation
                      }} />
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div key={index}>
                      <div style={{
                        height: 14,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        borderRadius: 4,
                        width: '60px',
                        marginBottom: 6,
                        animation: skeletonAnimation
                      }} />
                      <div style={{
                        height: 18,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        borderRadius: 4,
                        width: `${100 + Math.random() * 80}px`,
                        animation: skeletonAnimation
                      }} />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>

          {/* Recipients Table Skeleton */}
          <Card title="Recipients">
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <div style={{
                width: 120,
                height: 32,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                borderRadius: 6,
                animation: skeletonAnimation
              }} />
              <div style={{
                width: 140,
                height: 32,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                borderRadius: 6,
                animation: skeletonAnimation
              }} />
            </div>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}>
              <div style={{
                padding: '12px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                gap: 16
              }}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} style={{
                    height: 20,
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
                    borderRadius: 4,
                    flex: index === 0 ? '0 0 60px' : '1',
                    animation: skeletonAnimation
                  }} />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <div key={rowIndex} style={{
                  padding: '12px 16px',
                  borderBottom: rowIndex < 4 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center'
                }}>
                  {Array.from({ length: 6 }).map((_, colIndex) => (
                    <div key={colIndex} style={{
                      height: colIndex === 0 ? 24 : 16,
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                      borderRadius: 4,
                      flex: colIndex === 0 ? '0 0 60px' : '1',
                      animation: skeletonAnimation
                    }} />
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Right Column - Template Preview Skeleton */}
        <Col xs={24} lg={10}>
          <div className="campaign-preview-sticky" style={{ position: 'sticky', top: 20 }}>
            <Card title="Template Preview" size="small">
              {/* WhatsApp Preview Container Skeleton */}
              <div style={{
                background: '#f8f9fa',
                border: '2px solid #e9ecef',
                borderRadius: 24,
                padding: '16px 12px',
                maxWidth: 320,
                margin: '0 auto 16px',
                minHeight: 480
              }}>
                {/* Header Skeleton */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderBottom: '1px solid #e9ecef',
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(90deg, #25d366 25%, #20b558 50%, #25d366 75%)',
                    marginRight: 12,
                    animation: skeletonAnimation
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      height: 16,
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                      borderRadius: 4,
                      marginBottom: 4,
                      width: '120px',
                      animation: skeletonAnimation
                    }} />
                    <div style={{
                      height: 12,
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                      borderRadius: 4,
                      width: '80px',
                      animation: skeletonAnimation
                    }} />
                  </div>
                  <div style={{
                    width: 60,
                    height: 20,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    borderRadius: 10,
                    animation: skeletonAnimation
                  }} />
                </div>

                {/* Message Content Skeleton */}
                <div style={{ padding: '0 16px', marginBottom: 16 }}>
                  <div style={{
                    background: '#dcf8c6',
                    borderRadius: '12px 12px 4px 12px',
                    padding: 12,
                    marginLeft: 'auto',
                    maxWidth: '85%',
                    position: 'relative'
                  }}>
                    <div style={{
                      height: 16,
                      background: 'linear-gradient(90deg, #c8f7c5 25%, #b5e7b3 50%, #c8f7c5 75%)',
                      borderRadius: 4,
                      marginBottom: 8,
                      animation: skeletonAnimation
                    }} />
                    <div style={{
                      height: 16,
                      background: 'linear-gradient(90deg, #c8f7c5 25%, #b5e7b3 50%, #c8f7c5 75%)',
                      borderRadius: 4,
                      marginBottom: 8,
                      width: '90%',
                      animation: skeletonAnimation
                    }} />
                    <div style={{
                      height: 12,
                      background: 'linear-gradient(90deg, #c8f7c5 25%, #b5e7b3 50%, #c8f7c5 75%)',
                      borderRadius: 4,
                      width: '70%',
                      animation: skeletonAnimation
                    }} />
                    
                    {/* Message footer skeleton */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      marginTop: 8,
                      gap: 4
                    }}>
                      <div style={{
                        width: 40,
                        height: 12,
                        background: 'linear-gradient(90deg, #a8f5aa 25%, #95e297 50%, #a8f5aa 75%)',
                        borderRadius: 4,
                        animation: skeletonAnimation
                      }} />
                      <div style={{
                        width: 16,
                        height: 12,
                        background: 'linear-gradient(90deg, #4fc3f7 25%, #29b6f6 50%, #4fc3f7 75%)',
                        borderRadius: 4,
                        animation: skeletonAnimation
                      }} />
                    </div>
                  </div>
                </div>

                {/* Chat Input Skeleton */}
                <div style={{
                  padding: '8px 16px',
                  borderTop: '1px solid #e9ecef'
                }}>
                  <div style={{
                    height: 36,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    borderRadius: 18,
                    animation: skeletonAnimation
                  }} />
                </div>
              </div>

              {/* Template Content Skeleton */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                <div style={{
                  height: 16,
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  borderRadius: 4,
                  width: '120px',
                  marginBottom: 8,
                  animation: skeletonAnimation
                }} />
                <div style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 6,
                  minHeight: 80
                }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} style={{
                      height: 14,
                      background: 'linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)',
                      borderRadius: 4,
                      marginBottom: index < 2 ? 8 : 0,
                      width: `${70 + Math.random() * 25}%`,
                      animation: skeletonAnimation
                    }} />
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
  const [loading, setLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Add/Remove Recipients state
  const [addRecipientsVisible, setAddRecipientsVisible] = useState(false);
  const [removeRecipientsVisible, setRemoveRecipientsVisible] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPagination, setContactsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [addRemoveLoading, setAddRemoveLoading] = useState(false);
  const [removeValidationError, setRemoveValidationError] = useState('');

  // Media viewer (lightbox) state
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerUrl, setMediaViewerUrl] = useState('');
  const [mediaViewerType, setMediaViewerType] = useState('image');

  // WebSocket for real-time counter updates
  const wsContext = useWebSocketClientSafe();
  const prevStatusLenRef = useRef(0);

  // Fetch campaign statistics
  const fetchCampaignStats = async () => {
    try {
      setLoading(true);
      console.log('Fetching campaign stats for:', campaignId);
      const response = await axiosInstance.post('/campaign/stats', {
        campaignid: campaignId
      });

      console.log('Campaign stats response:', response.data);
      if (response.data.statuscode === 200) {
        const entity = response.data.entity || {};
        setCampaignStats(entity);
      } else {
        message.error(response.data.message || 'Failed to load campaign statistics');
      }
      // Expose BYO fields at entity root so the rest of the page can read them
      // (is_byo_webhook, byo_status, hard_send_cap are siblings of campaignDetails)
    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      message.error('Failed to load campaign statistics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaign transactions (recipients)
  const fetchTransactions = async (page = 1, pageSize = 10) => {
    try {
      setTransactionsLoading(true);
      console.log('Fetching transactions for campaign:', campaignId, 'Page:', page, 'PageSize:', pageSize);
      
      const response = await axiosInstance.post('/campaign/transactions', {
        campaignid: campaignId,
        page: page,
        pageSize: pageSize
      });

      console.log('Transactions response:', response.data);
      if (response.data.statuscode === 200) {
        setTransactions(response.data.entity.transactions || []);
        setTransactionsPagination({
          current: response.data.entity.page || 1,
          pageSize: response.data.entity.pageSize || 10,
          total: response.data.entity.total || 0
        });
      } else if (response.data.statuscode === 404) {
        // BYO webhook campaigns that haven't been sent yet have no transactions.
        // Backend returns 404 with an explanatory message — silence the toast.
        setTransactions([]);
      } else {
        message.error(response.data.message || 'Failed to load campaign transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      message.error('Failed to load campaign transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Media viewer helpers
  const openMediaViewer = (url, type = 'image') => {
    setMediaViewerUrl(url);
    setMediaViewerType(type);
    setMediaViewerOpen(true);
  };

  const closeMediaViewer = () => {
    setMediaViewerOpen(false);
    setMediaViewerUrl('');
    setMediaViewerType('image');
  };

  // Load data on component mount
  useEffect(() => {
    if (campaignId) {
      fetchCampaignStats();
      fetchTransactions();
    }
  }, [campaignId]);

  // WebSocket: live campaign counter updates (sent/delivered/seen/failed)
  useEffect(() => {
    const statusUpdates = wsContext?.statusUpdates;
    if (!statusUpdates || statusUpdates.length <= prevStatusLenRef.current) return;
    prevStatusLenRef.current = statusUpdates.length;

    const latest = statusUpdates[statusUpdates.length - 1];
    if (!latest || latest.campaignId !== campaignId) return;

    const STATUS_MAP = { sent: 1, failed: 2, delivered: 3, read: 4 };
    const statusNum = STATUS_MAP[latest.status];
    if (!statusNum) return;

    setCampaignStats(prev => {
      if (!prev || !prev.statusBreakdown) return prev;
      const breakdown = prev.statusBreakdown.map(s => {
        if (s.status === statusNum) return { ...s, count: (s.count || 0) + 1 };
        return s;
      });
      // If the status entry doesn't exist yet, add it
      if (!breakdown.find(s => s.status === statusNum)) {
        breakdown.push({ status: statusNum, count: 1 });
      }
      return { ...prev, statusBreakdown: breakdown };
    });
  }, [wsContext?.statusUpdates, campaignId]);

  // Fetch available contacts for adding to campaign
  const fetchAvailableContacts = async (page = 1, pageSize = 10) => {
    try {
      setContactsLoading(true);
      
      // Get bookid from campaign stats
      const bookid = campaignStats?.campaignDetails?.bookid;
      
      if (!bookid) {
        message.warning('Address book information not found for this campaign');
        setAvailableContacts([]);
        setContactsLoading(false);
        return;
      }

      const offset = (page - 1) * pageSize;

      const response = await axiosInstance.post('/addressbook/users', {
        bookid: bookid,
        limit: pageSize,
        offset: offset,
        searchby: searchText || '',
        labelfilter: ''
      });

      console.log('Contacts API Response:', response.data);

      if ((response.data?.statuscode === 200 || response.data?.status === true) && response.data?.entity) {
        const entity = response.data.entity;
        // Handle the API response structure where users are in entity.users
        const contactsData = entity.users || entity;
        
        // Filter out contacts that are already in the campaign (current transactions)
        const currentTransactionUserIds = new Set(transactions.map(t => t.userid));
        const availableContactsList = Array.isArray(contactsData) 
          ? contactsData.filter(contact => !currentTransactionUserIds.has(contact.userid))
          : [];
        
        setAvailableContacts(availableContactsList);
        
        // Get total count - check multiple possible locations in API response
        const totalCount = entity.total || entity.totalCount || entity.count || 
                          (Array.isArray(contactsData) ? contactsData.length : 0);
        
        console.log('Pagination Debug - Page:', page, 'PageSize:', pageSize, 'Total:', totalCount, 'Offset:', offset);
        
        // Update pagination state with total count
        setContactsPagination({
          current: page,
          pageSize: pageSize,
          total: totalCount
        });
      } else {
        message.error('Failed to load contacts from address book');
        setAvailableContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      message.error('Failed to load contacts from address book');
      setAvailableContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Handle contacts table pagination change
  const handleContactsTableChange = (paginationInfo, filters, sorter) => {
    console.log('Table pagination change:', paginationInfo);
    fetchAvailableContacts(paginationInfo.current, paginationInfo.pageSize);
  };

  // Add recipients to campaign
  const handleAddRecipients = async () => {
    if (selectedRecipients.length === 0) {
      message.warning('Please select at least one recipient to add');
      return;
    }

    try {
      setAddRemoveLoading(true);
      const response = await axiosInstance.post('/campaign/users/add', {
        campaignid: campaignId,
        userids: selectedRecipients
      });

      if (response.data?.statuscode === 200) {
        message.success(response.data.message);
        setAddRecipientsVisible(false);
        setSelectedRecipients([]);
        // Refresh campaign data
        fetchCampaignStats();
        fetchTransactions();
        // Notify parent component to refresh campaigns list
        if (onCampaignUpdated) {
          onCampaignUpdated();
        }
      } else {
        message.error(response.data?.message || 'Failed to add recipients');
      }
    } catch (error) {
      console.error('Error adding recipients:', error);
      message.error('Failed to add recipients');
    } finally {
      setAddRemoveLoading(false);
    }
  };

  // Remove recipients from campaign
  const handleRemoveRecipients = async () => {
    if (selectedRecipients.length === 0) {
      message.warning('Please select at least one recipient to remove');
      return;
    }

    // Inline validation: must keep at least 1 recipient
    const total = campaignStats?.totalUsers || 0;
    if (selectedRecipients.length >= total && total > 0) {
      setRemoveValidationError('You must keep at least 1 recipient in the campaign. Deselect at least one recipient.');
      return;
    }

    try {
      setAddRemoveLoading(true);
      const response = await axiosInstance.post('/campaign/users/remove', {
        campaignid: campaignId,
        userids: selectedRecipients
      });

      if (response.data?.statuscode === 200) {
        message.success(response.data.message);
        setRemoveRecipientsVisible(false);
        setSelectedRecipients([]);
        // Refresh campaign data
        fetchCampaignStats();
        fetchTransactions();
        // Notify parent component to refresh campaigns list
        if (onCampaignUpdated) {
          onCampaignUpdated();
        }
      } else {
        message.error(response.data?.message || 'Failed to remove recipients');
      }
    } catch (error) {
      console.error('Error removing recipients:', error);
      message.error('Failed to remove recipients');
    } finally {
      setAddRemoveLoading(false);
    }
  };

  // Handle pagination change
  const handleTableChange = (pagination) => {
    fetchTransactions(pagination.current, pagination.pageSize);
  };

  const templateBreakdown = useMemo(() => {
    const raw = campaignStats?.campaignDetails?.templatecontent;
    if (!raw) return null;

    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return null;
      }
    }

    if (!Array.isArray(parsed)) return null;

    const getComponent = (type) =>
      parsed.find((component) => String(component?.type || '').toUpperCase() === type);

    const header = getComponent('HEADER');
    const body = getComponent('BODY');
    const footer = getComponent('FOOTER');
    const buttons = getComponent('BUTTONS');

    return {
      headerText: header?.text || '',
      bodyText: body?.text || '',
      footerText: footer?.text || '',
      buttonTexts: Array.isArray(buttons?.buttons)
        ? buttons.buttons.map((button) => button?.text).filter(Boolean)
        : [],
    };
  }, [campaignStats?.campaignDetails?.templatecontent]);

  const previewMessageText = templateBreakdown?.bodyText
    || campaignStats?.campaignDetails?.templatecontent
    || 'Your campaign message will appear here...';

  const readableTemplateContent = useMemo(() => {
    if (templateBreakdown) {
      const sections = [];
      if (templateBreakdown.headerText) {
        sections.push(`HEADER\n${templateBreakdown.headerText}`);
      }
      if (templateBreakdown.bodyText) {
        sections.push(`BODY\n${templateBreakdown.bodyText}`);
      }
      if (templateBreakdown.footerText) {
        sections.push(`FOOTER\n${templateBreakdown.footerText}`);
      }
      if (templateBreakdown.buttonTexts.length > 0) {
        sections.push(`BUTTONS\n${templateBreakdown.buttonTexts.map((text, index) => `${index + 1}. ${text}`).join('\n')}`);
      }
      return sections.join('\n\n');
    }

    const raw = campaignStats?.campaignDetails?.templatecontent;
    if (typeof raw === 'string') return raw;
    if (raw == null) return 'No template content available';
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }, [templateBreakdown, campaignStats?.campaignDetails?.templatecontent]);

  // Generate WhatsApp Preview — redesigned phone frame
  const renderWhatsAppPreview = () => {
    // Resolve template variables from mapping
    let tplMap = null;
    try {
      if (campaignDetails?.templatemapping) {
        tplMap = typeof campaignDetails.templatemapping === 'string'
          ? JSON.parse(campaignDetails.templatemapping)
          : campaignDetails.templatemapping;
      }
    } catch {}

    const resolveVars = (text) => {
      if (!text || !tplMap?.mapping) return text;
      return Object.entries(tplMap.mapping).reduce((t, [variable, columnName]) => {
        return t.split(variable).join(columnName.replace('userdata.', ''));
      }, text);
    };

    const bodyText = resolveVars(templateBreakdown?.bodyText || previewMessageText);
    const footerText = templateBreakdown?.footerText;
    const buttonTexts = templateBreakdown?.buttonTexts || [];
    const category = (campaignDetails?.templatecategory || 'MARKETING').toUpperCase();
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    let companyName = 'MessageOS';
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (u?.name) companyName = u.name;
    } catch {}

    return (
      <div className="wa-phone">
        {/* Green WA header bar */}
        <div className="wa-bar">
          <div className="wa-avatar-wrap">
            <span className="wa-avatar-initials">{companyName.charAt(0).toUpperCase()}</span>
          </div>
          <div className="wa-bar-info">
            <span className="wa-bar-name">{companyName}</span>
            <span className="wa-bar-sub">Campaign Preview</span>
          </div>
          <span className={`wa-cat-badge wa-cat-badge--${category.toLowerCase()}`}>{category}</span>
        </div>

        {/* Chat area */}
        <div className="wa-chat">
          <div className="wa-msg-wrap">
            {/* Media header if present */}
            {(campaignStats?.header_media_url || campaignStats?.mediaUrl) && campaignStats?.headerFormat && (() => {
              const fmt = (campaignStats.headerFormat || '').toUpperCase();
              const mediaUrl = campaignStats.header_media_url || campaignStats.mediaUrl;
              if (fmt === 'IMAGE') return (
                <div className="wa-media-hdr" onClick={() => openMediaViewer(mediaUrl, 'image')}>
                  <img src={mediaUrl} alt="Header media" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              );
              if (fmt === 'VIDEO') return (
                <div className="wa-media-hdr">
                  <video src={mediaUrl} controls style={{ width: '100%', maxHeight: 180, display: 'block' }} />
                </div>
              );
              if (fmt === 'DOCUMENT') return (
                <div className="wa-doc-hdr" onClick={() => openMediaViewer(mediaUrl, 'document')}>
                  <span>📄</span><span>View Document</span>
                </div>
              );
              return null;
            })()}

            {/* Message bubble */}
            <div className="wa-bubble">
              <div className="wa-bubble-body">{bodyText || 'Your campaign message will appear here...'}</div>
              {footerText && <div className="wa-bubble-footer">{footerText}</div>}
              <div className="wa-bubble-meta">
                <span className="wa-bubble-time">{timeStr}</span>
                <svg width="14" height="11" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                </svg>
              </div>
            </div>

            {/* CTA buttons rendered as separate tappable pill rows */}
            {buttonTexts.length > 0 && (
              <div className="wa-ctabtns">
                {buttonTexts.map((text, i) => (
                  <div key={i} className="wa-ctabtn">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6, flexShrink: 0 }}>
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
                    </svg>
                    {text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Fake input bar */}
        <div className="wa-inputbar">
          <div className="wa-inputbox">
            <span className="wa-placeholder">Type a message…</span>
          </div>
          <button className="wa-send" type="button" aria-label="Send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Table columns for transactions
  const transactionColumns = [
    {
      title: 'Sr No',
      key: 'index',
      width: 60,
      render: (_, record, index) => (
        <span style={{ fontWeight: 500 }}>
          {(transactionsPagination.current - 1) * transactionsPagination.pageSize + index + 1}
        </span>
      ),
    },
    {
      title: 'Recipient',
      key: 'recipient',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.username}</div>
            <div style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {record.emailid}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Mobile Number',
      key: 'mobile',
      width: 130,
      render: (_, record) => (
        <span style={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13 }}>
          +{record.countrycode} {record.mobileno}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'emailid',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (email) => (
        <Tooltip title={email}>
          <span style={{ fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>{email}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => {
        const statusConfig = {
          1: { color: 'success', text: 'Sent', icon: <CheckCircleOutlined /> },
          2: { color: 'error', text: 'Failed', icon: <CloseCircleOutlined /> },
          3: { color: 'processing', text: 'Delivered', icon: <CheckCircleOutlined /> },
          4: { color: 'purple', text: 'Seen', icon: <EyeOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', text: 'Pending', icon: <ClockCircleOutlined /> };
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <Tag color={config.color} icon={config.icon} style={{ margin: 0 }}>
              {config.text}
            </Tag>
            {record.is_opted_out && (
              <Tag color="red" style={{ fontSize: '10px', marginTop: 2, display: 'block', width: 'fit-content' }}>
                Opted out{record.opted_out_category ? ` (${record.opted_out_category})` : ''}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Date & Time',
      dataIndex: 'entrytime',
      key: 'entrytime',
      width: 120,
      render: (entrytime) => (
        <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
          {moment(entrytime).format('MMM DD, YYYY')}<br />
          <span style={{ color: '#999' }}>{moment(entrytime).format('h:mm A')}</span>
        </span>
      ),
    },
    {
      title: 'Failure Code',
      dataIndex: 'error_code',
      key: 'error_code',
      width: 130,
      render: (text, record) =>
        record.status === 2 ? (
          <Tag color="error" icon={<WarningOutlined />} style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {text || '—'}
          </Tag>
        ) : null,
    },
    {
      title: 'Failure Reason',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 220,
      render: (text, record) =>
        record.status === 2 ? (
          <Tooltip title={text || '—'}>
            <span style={{ color: '#ff4d4f', fontSize: 12, cursor: 'default' }}>
              {text
                ? text.length > 60
                  ? text.slice(0, 60) + '…'
                  : text
                : '—'}
            </span>
          </Tooltip>
        ) : null,
    },
  ];

  // Filtered view for the table
  const displayedTransactions = showOnlyFailed
    ? transactions.filter(t => t.status === 2)
    : transactions;

  // Export failed rows as CSV
  const exportFailuresCSV = () => {
    const failed = transactions.filter(t => t.status === 2);
    if (failed.length === 0) {
      message.info('No failed transactions to export.');
      return;
    }
    const headers = ['Sr No', 'Name', 'Email', 'Mobile', 'Error Code', 'Failure Reason', 'Date'];
    const rows = failed.map((t, i) => [
      i + 1,
      `"${(t.username || '').replace(/"/g, '""')}"`,
      `"${(t.emailid || '').replace(/"/g, '""')}"`,
      `+${t.countrycode} ${t.mobileno}`,
      `"${(t.error_code || '').replace(/"/g, '""')}"`,
      `"${(t.error_message || '').replace(/"/g, '""')}"`,
      moment(t.entrytime).format('MMM DD YYYY h:mm A'),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `failed_recipients_${campaignId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <CampaignDetailsSkeleton />;
  }

  if (!campaignStats) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Alert
          message="Campaign Not Found"
          description="The requested campaign could not be found."
          type="error"
          showIcon
        />
      </div>
    );
  }
  const {
    campaignDetails,
    statusBreakdown,
    totalUsers,
    is_byo_webhook,
    byo_status,
    hard_send_cap,
  } = campaignStats;

  const isByo = !!is_byo_webhook || campaignDetails?.source === 'byo_webhook';
  const displayStatus = getCampaignDisplayStatus(campaignDetails);
  const scheduledAt = campaignDetails?.scheduled_at || campaignDetails?.scheduledAt || null;

  // Get status counts (1: Sent, 2: Failed, 3: Delivered, 4: Seen)
  const sentCount = statusBreakdown?.find(s => s.status === 1)?.count || 0;
  const failedCount = statusBreakdown?.find(s => s.status === 2)?.count || 0;
  const deliveredCount = statusBreakdown?.find(s => s.status === 3)?.count || 0;
  const seenCount = statusBreakdown?.find(s => s.status === 4)?.count || 0;
  // For BYO campaigns that haven't been sent, totalUsers is 0 — don't show negative pending
  const pendingCount = isByo && campaignDetails?.issent === 0
    ? 0
    : Math.max(0, (totalUsers || 0) - sentCount - failedCount - deliveredCount - seenCount);

  return (
    <div className="cdp">
      {/* ── Page header (no card) ─────────────────────────────────── */}
      <div className="cdp__hd">
        <button className="cdp__back" onClick={onBack}>
          <ArrowLeftOutlined />
          <span>Back</span>
        </button>
        <div className="cdp__hd-body">
          <div className="cdp__hd-row">
            <h1 className="cdp__hd-name">{campaignDetails.campaignname}</h1>
            <div className="cdp__hd-badges">
              <span className="cdp__badge cdp__badge--cat">
                {(campaignDetails.templatecategory || 'Marketing').charAt(0).toUpperCase() +
                  (campaignDetails.templatecategory || 'marketing').slice(1).toLowerCase()}
              </span>
              <span className={`cdp__badge ${displayStatus.className}`}>
                <span className="cdp__pulse" />
                {displayStatus.label}
              </span>
              {isByo && <span className="cdp__badge cdp__badge--byo">BYO Webhook</span>}
              {isByo && byo_status && byo_status !== 'draft' && (
                <span className={`cdp__badge ${byo_status === 'completed' ? 'cdp__badge--sent' : 'cdp__badge--draft'}`}>
                  {byo_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
            </div>
          </div>
          <div className="cdp__hd-meta">
            <span className="cdp__meta-item">
              {moment(campaignDetails.entrytime).format('MMM D, YYYY [at] h:mm A')}
            </span>
            <span className="cdp__meta-dot" aria-hidden="true">·</span>
            <span className="cdp__meta-item">
              <svg className="cdp__meta-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
              {campaignDetails.source === 'byo_webhook' ? 'BYO Webhook' : 'Address Book'}
            </span>
            <span className="cdp__meta-dot" aria-hidden="true">·</span>
            <code className="cdp__meta-tpl">{campaignDetails.templatename}</code>
            <span className="cdp__meta-dot" aria-hidden="true">·</span>
            <Tooltip title="Copy campaign ID">
              <button
                className="cdp__copy-id"
                onClick={() => {
                  navigator.clipboard.writeText(String(campaignId || campaignDetails.campaignid || ''));
                  message.success('Campaign ID copied');
                }}
              >
                <CopyOutlined />
                <span>ID: {String(campaignId || campaignDetails.campaignid || '').slice(0, 8)}</span>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* ── Two-column grid ────────────────────────────────────────── */}
      <div className="cdp__grid">

        {/* LEFT COLUMN */}
        <div className="cdp__left">
          {isByo && campaignDetails?.issent === 0 && (
            <div className="cdp__byo-notice">
              <InfoCircleOutlined />
              <span>
                Recipients will be fetched from your webhook at send time
                {hard_send_cap ? ` · Up to ${hard_send_cap.toLocaleString()} recipients` : ''}
              </span>
            </div>
          )}

          {/* ── Stats strip ──────────────────────────────────────── */}
          <div className="cdp__stats">
            <div className="cdp__stat cdp__stat--neutral">
              <span className="cdp__stat-lbl">Total recipients</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><TeamOutlined /></span>
                <span className="cdp__stat-val">
                  {isByo && campaignDetails?.issent === 0
                    ? (hard_send_cap ? hard_send_cap.toLocaleString() : '—')
                    : totalUsers}
                </span>
              </div>
            </div>
            <div className="cdp__stat cdp__stat--blue">
              <span className="cdp__stat-lbl">Seen</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><EyeOutlined /></span>
                <span className="cdp__stat-val">{seenCount}</span>
              </div>
            </div>
            <div className="cdp__stat cdp__stat--green">
              <span className="cdp__stat-lbl">Delivered</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><CheckCircleOutlined /></span>
                <span className="cdp__stat-val">{deliveredCount}</span>
              </div>
            </div>
            <div className="cdp__stat cdp__stat--neutral">
              <span className="cdp__stat-lbl">Sent</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><CheckCircleOutlined /></span>
                <span className="cdp__stat-val">{sentCount}</span>
              </div>
            </div>
            <div className="cdp__stat cdp__stat--amber">
              <span className="cdp__stat-lbl">Pending</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><ClockCircleOutlined /></span>
                <span className="cdp__stat-val">{pendingCount}</span>
              </div>
            </div>
            <div className="cdp__stat cdp__stat--red">
              <span className="cdp__stat-lbl">Failed</span>
              <div className="cdp__stat-row">
                <span className="cdp__stat-icon"><CloseCircleOutlined /></span>
                <span className="cdp__stat-val">{failedCount}</span>
              </div>
            </div>
            {campaignStats.optedOutUsers > 0 && (
              <div className="cdp__stat cdp__stat--red">
                <span className="cdp__stat-lbl">Opted out</span>
                <div className="cdp__stat-row">
                  <span className="cdp__stat-icon"><StopOutlined /></span>
                  <span className="cdp__stat-val">{campaignStats.optedOutUsers}</span>
                </div>
              </div>
            )}
            {campaignStats.effectiveUsers != null && campaignStats.effectiveUsers !== totalUsers && (
              <div className="cdp__stat cdp__stat--green">
                <span className="cdp__stat-lbl">Effective</span>
                <div className="cdp__stat-row">
                  <span className="cdp__stat-icon"><TeamOutlined /></span>
                  <span className="cdp__stat-val">{campaignStats.effectiveUsers}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── Campaign meta + variable mapping ─────────────── */}
          <div className="cdp__info-card">
            <div className="cdp__info-grid">
              <div className="cdp__info-item">
                <span className="cdp__info-lbl">Created</span>
                <span className="cdp__info-val">{moment(campaignDetails.entrytime).format('MMM D, YYYY h:mm A')}</span>
              </div>
              <div className="cdp__info-item">
                <span className="cdp__info-lbl">Template name</span>
                <code className="cdp__info-mono">{campaignDetails.templatename}</code>
              </div>
              <div className="cdp__info-item">
                <span className="cdp__info-lbl">Category</span>
                <span className="cdp__badge cdp__badge--cat">
                  {(campaignDetails.templatecategory || 'Marketing').charAt(0).toUpperCase() +
                    (campaignDetails.templatecategory || 'marketing').slice(1).toLowerCase()}
                </span>
              </div>
              <div className="cdp__info-item">
                <span className="cdp__info-lbl">Source</span>
                <span className="cdp__info-val">
                  {campaignDetails.source === 'byo_webhook' ? 'BYO Webhook' : 'Address Book'}
                </span>
              </div>
              <div className="cdp__info-item">
                <span className="cdp__info-lbl">Status</span>
                <span className={`cdp__badge ${displayStatus.className}`}>
                  {displayStatus.label}
                </span>
              </div>
              {scheduledAt && displayStatus.scheduleStatus && (
                <div className="cdp__info-item">
                  <span className="cdp__info-lbl">Scheduled for</span>
                  <span className="cdp__info-val">{moment(scheduledAt).format('MMM D, YYYY [at] h:mm A')}</span>
                </div>
              )}
              {campaignDetails.source === 'byo_webhook' && campaignDetails.audience_webhook_url && (
                <div className="cdp__info-item cdp__info-item--full">
                  <span className="cdp__info-lbl">Webhook URL</span>
                  <code className="cdp__info-mono">{campaignDetails.audience_webhook_url}</code>
                </div>
              )}
            </div>

            {/* Variable mapping chips */}
            {(() => {
              let tplMap = null;
              try {
                if (campaignDetails.templatemapping) {
                  tplMap = typeof campaignDetails.templatemapping === 'string'
                    ? JSON.parse(campaignDetails.templatemapping)
                    : campaignDetails.templatemapping;
                }
              } catch {}

              if (tplMap?.mapping && Object.keys(tplMap.mapping).length > 0) {
                return (
                  <div className="cdp__mapping">
                    <span className="cdp__section-lbl">Variable mapping</span>
                    <div className="cdp__chips">
                      {Object.entries(tplMap.mapping).map(([variable, columnName]) => (
                        <span key={variable} className="cdp__chip">
                          <code className="cdp__chip-var">{variable}</code>
                          <span className="cdp__chip-arrow">→</span>
                          <span className="cdp__chip-val">{columnName.replace('userdata.', '')}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }

              if (campaignDetails.component?.length > 0) {
                const hasVars = campaignDetails.component.some(c => c.example?.body_text);
                if (!hasVars) return null;
                return (
                  <div className="cdp__mapping">
                    <span className="cdp__section-lbl">Template variables</span>
                    <div className="cdp__chips">
                      {campaignDetails.component.flatMap((comp) =>
                        (comp.example?.body_text || []).flatMap((examples, i) =>
                          examples.map((ex, j) => (
                            <span key={`${i}-${j}`} className="cdp__chip">
                              <code className="cdp__chip-var">{`{{${i + 1}}}`}</code>
                              <span className="cdp__chip-arrow">→</span>
                              <span className="cdp__chip-val">{ex}</span>
                            </span>
                          ))
                        )
                      )}
                    </div>
                  </div>
                );
              }

              return null;
            })()}
          </div>

          {/* ── Rate details banner ─────────────────────────── */}
          {campaignStats.ratedetails && (
            <div className="cdp__rate-banner">
              <InfoCircleOutlined className="cdp__rate-icon" />
              <span>
                Campaign cost: ₹{campaignStats.ratedetails.total_cost ?? campaignStats.ratedetails.campaign_cost ?? '—'}
                {' · '}Meta cost: ₹{campaignStats.ratedetails.meta_cost ?? campaignStats.ratedetails.meta_rate ?? '—'}
                {' · '}{totalUsers} contact{totalUsers !== 1 ? 's' : ''}
                {campaignStats.ratedetails.countrycode ? ` · +${campaignStats.ratedetails.countrycode} country code` : ''}
              </span>
            </div>
          )}

          {/* ── Recipients ──────────────────────────────────── */}
          {campaignDetails.source === 'byo_webhook' ? (
            <div className="cdp__recipients-card">
              <div className="cdp__rec-hd">
                <span className="cdp__rec-title">Recipients (Streamed via Webhook)</span>
              </div>
              <div style={{ padding: '0 0 4px' }}>
                <RedactedRecipientsTable
                  campaignId={campaignDetails.campaignid || campaignId}
                  source="byo_webhook"
                />
              </div>
            </div>
          ) : (
            <div className="cdp__recipients-card">
              <div className="cdp__rec-hd">
                <span className="cdp__rec-title">Recipients</span>
                <div className="cdp__rec-actions">
                  <label className="cdp__failed-toggle">
                    <Checkbox
                      checked={showOnlyFailed}
                      onChange={e => setShowOnlyFailed(e.target.checked)}
                    />
                    <span>Show only failed</span>
                  </label>
                  <button className="cdp__rec-btn cdp__rec-btn--outline-danger" onClick={exportFailuresCSV}>
                    <WarningOutlined /> Export failures
                  </button>
                  <Tooltip title={campaignDetails.issent === 1 ? 'Cannot add to a sent campaign' : 'Add recipients'}>
                    <button
                      className="cdp__rec-btn cdp__rec-btn--outline"
                      onClick={() => { setAddRecipientsVisible(true); fetchAvailableContacts(); }}
                      disabled={campaignDetails.issent === 1}
                    >
                      <PlusOutlined /> Add recipients
                    </button>
                  </Tooltip>
                  <Tooltip title={
                    campaignDetails.issent === 1 ? 'Cannot remove from sent campaign'
                      : selectedRecipients.length === 0 ? 'Select recipients to remove'
                      : 'Remove selected recipients'
                  }>
                    <button
                      className="cdp__rec-btn cdp__rec-btn--outline"
                      onClick={() => setRemoveRecipientsVisible(true)}
                      disabled={selectedRecipients.length === 0 || campaignDetails.issent === 1}
                    >
                      <MinusOutlined /> Remove recipients
                    </button>
                  </Tooltip>
                </div>
              </div>

              {removeValidationError && (
                <Alert
                  type="error"
                  message={removeValidationError}
                  showIcon
                  closable
                  onClose={() => setRemoveValidationError('')}
                  style={{ margin: '0 16px 12px', borderRadius: 8 }}
                />
              )}

              <Table
                className="cdp__table"
                columns={transactionColumns}
                dataSource={displayedTransactions}
                rowKey="userid"
                loading={transactionsLoading}
                rowSelection={{
                  selectedRowKeys: selectedRecipients,
                  onChange: (keys) => {
                    const total = campaignStats?.totalUsers || 0;
                    if (total > 0 && keys.length >= total) {
                      setRemoveValidationError('You must keep at least 1 recipient. Deselect at least one.');
                      return;
                    }
                    setRemoveValidationError('');
                    setSelectedRecipients(keys);
                  },
                }}
                pagination={{
                  ...transactionsPagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} recipients`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
              />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — sticky preview */}
        <div className="cdp__right">
          <div className="cdp__preview-sticky">
            <div className="cdp__preview-card">
              <div className="cdp__preview-hd">
                <span className="cdp__preview-title">Template preview</span>
                <span className="cdp__preview-badge">WhatsApp UI</span>
              </div>
              <div className="cdp__preview-body">
                {renderWhatsAppPreview()}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Add Recipients Modal */}
      <Modal
        title="Add Recipients to Campaign"
        open={addRecipientsVisible}
        onCancel={() => {
          setAddRecipientsVisible(false);
          setSelectedRecipients([]);
          setSearchText('');
          setContactsPagination({
            current: 1,
            pageSize: 10,
            total: 0
          });
        }}
        onOk={handleAddRecipients}
        confirmLoading={addRemoveLoading}
        width={800}
        okText={`Add ${selectedRecipients.length} Recipient(s)`}
        okButtonProps={{ disabled: selectedRecipients.length === 0 }}
      >
        {campaignStats?.campaignDetails?.bookid && (
          <Alert
            message="Address Book Contacts"
            description="You can only add contacts from the same address book that was used when creating this campaign."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search contacts in this address book..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => fetchAvailableContacts(1, contactsPagination.pageSize)}
            allowClear
            style={{ marginBottom: 8 }}
          />
          <Button onClick={() => fetchAvailableContacts(1, contactsPagination.pageSize)} loading={contactsLoading}>
            Search
          </Button>
        </div>
        
        <Table
          dataSource={availableContacts}
          rowKey="userid"
          loading={contactsLoading}
          rowSelection={{
            selectedRowKeys: selectedRecipients,
            onChange: (selectedRowKeys) => {
              setSelectedRecipients(selectedRowKeys);
            },
            preserveSelectedRowKeys: true,
          }}
          pagination={{
            current: contactsPagination.current,
            pageSize: contactsPagination.pageSize,
            total: contactsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts available`,
          }}
          onChange={handleContactsTableChange}
          size="small"
          locale={{
            emptyText: contactsLoading ? 'Loading...' : 'No contacts available in this address book'
          }}
          columns={[
            {
              title: 'Name',
              dataIndex: 'username',
              key: 'username',
              render: (name, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar icon={<UserOutlined />} size="small" style={{ marginRight: 8 }} />
                  <div>
                    <div>{name || 'N/A'}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {record.usercategory || record.userdata?.category || '-'}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              title: 'Mobile',
              dataIndex: 'mobileno',
              key: 'mobileno',
              render: (mobile, record) => {
                const countryCode = record.countrycode || record.userdata?.countrycode || '';
                return mobile ? `${countryCode} ${mobile}` : 'N/A';
              },
            },
            {
              title: 'Email',
              dataIndex: 'emailid',
              key: 'emailid',
              render: (email, record) => email || record.userdata?.emailid || 'N/A',
            },
          ]}
        />
      </Modal>

      {/* Remove Recipients Modal */}
      <Modal
        title="Remove Recipients from Campaign"
        open={removeRecipientsVisible}
        onCancel={() => {
          setRemoveRecipientsVisible(false);
          setSelectedRecipients([]);
          setRemoveValidationError('');
        }}
        onOk={handleRemoveRecipients}
        confirmLoading={addRemoveLoading}
        okText={`Remove ${selectedRecipients.length} Recipient(s)`}
        okButtonProps={{ 
          disabled: selectedRecipients.length === 0 || ((campaignStats?.totalUsers || 0) > 0 && selectedRecipients.length >= (campaignStats?.totalUsers || 0)),
          danger: true 
        }}
        okType="danger"
      >
        <Alert
          message="Confirm Removal"
          description={`Are you sure you want to remove ${selectedRecipients.length} selected recipient(s) from this campaign? This action cannot be undone.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <div>
          <Text strong>Selected Recipients: </Text>
          <Text>{selectedRecipients.length} recipient(s) selected from the table above.</Text>
        </div>
        {(campaignStats?.totalUsers || 0) > 0 && selectedRecipients.length >= (campaignStats?.totalUsers || 0) && (
          <div style={{ color: '#ff4d4f', marginTop: 12 }}>
            You must keep at least 1 recipient in the campaign. Deselect at least one recipient.
          </div>
        )}
      </Modal>

      {/* Media Viewer Modal (Lightbox) */}
      <Modal
        open={mediaViewerOpen}
        onCancel={closeMediaViewer}
        footer={null}
        width="90%"
        style={{ maxWidth: 900, top: 20 }}
        centered
        zIndex={2000}
        bodyStyle={{
          padding: 0,
          background: 'transparent',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
        closeIcon={
          <div style={{
            background: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 18
          }}>✕</div>
        }
        modalRender={(modal) => (
          <div style={{ background: 'transparent' }}>{modal}</div>
        )}
        styles={{
          mask: { background: 'rgba(0,0,0,0.92)', zIndex: 2000 },
          content: { background: 'transparent', boxShadow: 'none' }
        }}
      >
        {mediaViewerType === 'image' && mediaViewerUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <img
              src={mediaViewerUrl}
              alt="Campaign media"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="color:#fff;text-align:center;padding:40px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><div style="font-size:16px;">Image failed to load</div><div style="font-size:13px;opacity:0.7;margin-top:8px;">The URL may have expired. Try refreshing.</div></div>';
              }}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <Button
                type="primary"
                onClick={() => window.open(mediaViewerUrl, '_blank')}
                style={{ background: '#1890ff' }}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        )}
        {mediaViewerType === 'video' && mediaViewerUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <video
              src={mediaViewerUrl}
              controls
              autoPlay
              style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 8, background: '#000' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="color:#fff;text-align:center;padding:40px;"><div style="font-size:48px;margin-bottom:16px;">⚠️</div><div style="font-size:16px;">Video failed to load</div><div style="font-size:13px;opacity:0.7;margin-top:8px;">The URL may have expired. Try refreshing.</div></div>';
              }}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <Button
                type="primary"
                onClick={() => window.open(mediaViewerUrl, '_blank')}
                style={{ background: '#1890ff' }}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        )}
        {mediaViewerType === 'document' && mediaViewerUrl && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: 40 }}>
            <div style={{ fontSize: 64, marginBottom: 24 }}>📄</div>
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: 500, marginBottom: 8 }}>Document Preview</Text>
            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 24 }}>
              Click below to open or download the document
            </Text>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                type="primary"
                size="large"
                onClick={() => window.open(mediaViewerUrl, '_blank')}
                style={{ background: '#1890ff' }}
              >
                Open Document
              </Button>
              <Button
                size="large"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = mediaViewerUrl;
                  link.download = 'document';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ borderColor: '#fff', color: '#fff' }}
                ghost
              >
                Download
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CampaignDetailsPage;

